import JSZip from 'jszip';
import {
  assessUpload,
  isUploadManifestPath,
  MAX_UPLOAD_MANIFEST_BYTES,
  type AssessUploadInput,
  type UploadAssessment,
  type UploadEntry,
} from './uploadRules';

type ZipData = Blob | ArrayBuffer | Uint8Array;

/** JSZip keeps the central directory sizes on a private field. */
function uncompressedSizeOf(entry: JSZip.JSZipObject): number | undefined {
  const size = (entry as { _data?: { uncompressedSize?: unknown } })._data
    ?.uncompressedSize;
  return typeof size === 'number' ? size : undefined;
}

declare module 'jszip' {
  /** Documented JSZip API that its typings leave out. */
  interface JSZipObject {
    internalStream(type: 'uint8array'): JSZip.JSZipStreamHelper<Uint8Array>;
  }
}

/**
 * Inflates a text entry, or returns `undefined` once it grows past
 * `maxBytes`. The size in the zip header can lie, so it can't bound memory.
 */
function readTextUpTo(
  entry: JSZip.JSZipObject,
  maxBytes: number,
): Promise<string | undefined> {
  const stream = entry.internalStream('uint8array');
  return new Promise((resolve, reject) => {
    const chunks: Uint8Array[] = [];
    let size = 0;
    stream
      .on('data', chunk => {
        size += chunk.length;
        if (size > maxBytes) {
          stream.pause();
          resolve(undefined);
          return;
        }
        chunks.push(chunk);
      })
      .on('error', reject)
      .on('end', () => {
        const bytes = new Uint8Array(size);
        let offset = 0;
        for (const chunk of chunks) {
          bytes.set(chunk, offset);
          offset += chunk.length;
        }
        resolve(new TextDecoder().decode(bytes));
      })
      .resume();
  });
}

/**
 * Lists a zip's files and reads its small manifests, without inflating
 * anything else.
 */
export async function readUploadZip(data: ZipData): Promise<AssessUploadInput> {
  const zip = await JSZip.loadAsync(data);
  const entries: UploadEntry[] = [];
  const manifests: Record<string, string> = {};
  for (const entry of Object.values(zip.files)) {
    if (entry.dir) continue;
    const sizeBytes = uncompressedSizeOf(entry);
    entries.push({ path: entry.name, sizeBytes });
    if (
      isUploadManifestPath(entry.name) &&
      sizeBytes !== undefined &&
      sizeBytes <= MAX_UPLOAD_MANIFEST_BYTES
    ) {
      const text = await readTextUpTo(entry, MAX_UPLOAD_MANIFEST_BYTES);
      if (text !== undefined) manifests[entry.name] = text;
    }
  }
  return { entries, manifests };
}

export async function assessUploadZip(
  data: ZipData,
): Promise<UploadAssessment> {
  return assessUpload(await readUploadZip(data));
}
